import { addBusinessDays, format } from "date-fns";
import React, { useMemo } from "react";

import { frequencyToQuestionCount, selectReminderCadence } from "@performyard/engagement";
import { Text } from "@performyard/py-components";

import { useEngagementSurveyCustomQuestions } from "../../../../../../api/hooks/defs/engagement-survey-custom-questions";
import { useFeatureFlagQuery } from "../../../../../../hooks/useFeatureFlag";

type EngagementSettingsSurveyScheduleProps = {
	frequency: number;
	nextSurveyDate: string | undefined;
	questionCycle: number;
	factorsFrequency: number;
	enpsFrequency: number;
	customQuestionsFrequency: number;
	customQuestionsIncludeAll: boolean;
	additionalFeedbackQuestionFrequency: number;
	sendReminderEmails: boolean;
	manuallySelectedQuestions: Array<{ question_id: string; order: number }>;
	manuallySelectedQuestionsFrequency?: number;
	selectionMethod: "automatic" | "manual";
};

const EngagementSettingsSurveySchedule = ({
	frequency,
	nextSurveyDate,
	questionCycle,
	factorsFrequency,
	enpsFrequency,
	customQuestionsFrequency,
	customQuestionsIncludeAll,
	additionalFeedbackQuestionFrequency,
	sendReminderEmails,
	manuallySelectedQuestions,
	manuallySelectedQuestionsFrequency = 1,
	selectionMethod,
}: EngagementSettingsSurveyScheduleProps) => {
	const { data: customQuestions, isLoading } = useEngagementSurveyCustomQuestions();
	const { value: isEngagementQuestionSelectionEnabled } = useFeatureFlagQuery(
		"ff-temp-is-engagement-question-selection-enabled",
		false
	);

	const customQuestionsCount = useMemo(() => {
		return customQuestions?.results.filter((q) => q.enabled).length ?? 0;
	}, [JSON.stringify(customQuestions), isLoading]);

	const nextDate = useMemo(() => {
		return nextSurveyDate ? new Date(nextSurveyDate) : null;
	}, [nextSurveyDate]);

	const remindersCadence = useMemo(() => {
		return nextDate ? selectReminderCadence(frequency) : [];
	}, [nextDate, frequency]);

	const nextFourDates = useMemo(() => {
		if (!nextDate) {
			return [];
		}
		return [0, 1, 2, 3].map((i) => {
			const date = new Date(nextDate);
			date.setDate(date.getDate() + i * frequency);
			return date;
		});
	}, [nextDate, frequency]);

	if (!nextSurveyDate || !nextDate) {
		return null;
	}

	const displayQuestionFrequencies = (current: number) => {
		// Handle manual selection mode
		if (selectionMethod === "manual" && isEngagementQuestionSelectionEnabled) {
			const enpsEnabled = enpsFrequency !== 0 && (questionCycle + current) % enpsFrequency === 0;
			const manualQuestionsEnabled = (questionCycle + current) % manuallySelectedQuestionsFrequency === 0;
			const includeAdditionalFeedbackQuestion =
				(questionCycle + current) % additionalFeedbackQuestionFrequency === 0;
			const manualQuestionsCount = manualQuestionsEnabled ? (manuallySelectedQuestions?.length ?? 0) : 0;

			const totalQuestions =
				(enpsEnabled ? 1 : 0) + manualQuestionsCount + (includeAdditionalFeedbackQuestion ? 1 : 0);

			if (totalQuestions === 0) {
				return "0 Questions";
			}

			const parts: string[] = [];
			if (enpsEnabled) {
				parts.push("eNPS");
			}
			if (manualQuestionsCount > 0) {
				parts.push("Manually Selected Questions");
			}
			if (includeAdditionalFeedbackQuestion) {
				parts.push("Feedback");
			}

			return `${parts.join(" + ")}, ${totalQuestions} Question${totalQuestions !== 1 ? "s" : ""}`;
		}

		// Automatic selection mode
		const baseQuestionsCount = frequencyToQuestionCount(frequency);
		const includeCustomQuestions = (questionCycle + current) % customQuestionsFrequency === 0;
		const includeAdditionalFeedbackQuestion = (questionCycle + current) % additionalFeedbackQuestionFrequency === 0;
		const factorsEnabled = (questionCycle + current) % factorsFrequency === 0;
		const enpsEnabled = enpsFrequency !== 0 && (questionCycle + current) % enpsFrequency === 0;

		const parts: string[] = [];
		let totalQuestions = 0;

		if (factorsEnabled) {
			parts.push("Factors");
			totalQuestions = baseQuestionsCount;
		}

		if (enpsEnabled) {
			parts.push("eNPS");
			// If factors are enabled, eNPS is already included in baseQuestionsCount
			// If factors are not enabled, eNPS adds 1 question
			if (!factorsEnabled) {
				totalQuestions += 1;
			}
		}

		if (includeCustomQuestions) {
			parts.push("Custom");
			// Only add customQuestionsCount if including all custom questions
			// Otherwise, custom questions are already included in baseQuestionsCount
			if (customQuestionsIncludeAll) {
				totalQuestions += customQuestionsCount;
			}
		}

		if (includeAdditionalFeedbackQuestion) {
			parts.push("Feedback");
			totalQuestions += 1;
		}

		if (totalQuestions === 0) {
			return "0 Questions";
		}

		return `${parts.join(" + ")}, ${totalQuestions} Question${totalQuestions !== 1 ? "s" : ""}`;
	};

	return (
		<div
			className="flex w-full flex-col gap-2 rounded-[4px] bg-neutral-2 p-2"
			data-testid="engagement-settings-survey-schedule"
		>
			{/* Survey Schedule Section */}
			<div>
				<div className="flex gap-1 whitespace-nowrap">
					<Text size="caption" weight="semi-bold" color="neutral-9">
						Pulse Survey Schedule:
					</Text>
				</div>
				{nextFourDates.map((date, i) => {
					return (
						<div key={`${date.toString()}-${i}`} className="flex items-center gap-2 pl-2">
							<Text size="caption" weight="regular" color="neutral-9">
								•
							</Text>
							<Text size="caption" weight="regular" color="neutral-9">
								{format(date, "EEEE MMM d, yyyy")} ({displayQuestionFrequencies(i)})
							</Text>
						</div>
					);
				})}
			</div>

			{/* Reminder Schedule Section - Only show if reminders are enabled */}
			{sendReminderEmails && frequency && nextSurveyDate && (
				<div>
					<Text size="caption" weight="semi-bold" color="neutral-9">
						Next Pulse Survey Reminder Schedule:{" "}
					</Text>
					{remindersCadence.length > 0 && (
						<div className="flex items-center gap-2 pl-2">
							<Text size="caption" weight="semi-bold" color="neutral-9" overrideElement="span">
								• First Reminder:{" "}
							</Text>{" "}
							<Text size="caption" weight="regular" color="neutral-9" overrideElement="span">
								{format(addBusinessDays(nextDate, remindersCadence[0]), "EEEE, MMM d, yyyy")}
							</Text>
						</div>
					)}
					{remindersCadence.length > 1 && (
						<div className="flex items-center gap-2 pl-2">
							<Text size="caption" weight="semi-bold" color="neutral-9" overrideElement="span">
								• Second Reminder:{" "}
							</Text>{" "}
							<Text size="caption" weight="regular" color="neutral-9" overrideElement="span">
								{format(addBusinessDays(nextDate, remindersCadence[1]), "EEEE, MMM d, yyyy")}
							</Text>
						</div>
					)}
					{remindersCadence.length > 2 && (
						<div className="flex items-center gap-2 pl-2">
							<Text size="caption" weight="semi-bold" color="neutral-9" overrideElement="span">
								• Third Reminder:{" "}
							</Text>{" "}
							<Text size="caption" weight="regular" color="neutral-9" overrideElement="span">
								{format(addBusinessDays(nextDate, remindersCadence[2]), "EEEE, MMM d, yyyy")}
							</Text>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default EngagementSettingsSurveySchedule;
